import { useTranslation } from "react-i18next";
import { Alert } from "../../../../types/Alert";
import { useState, useEffect } from "react";
import DeviceService from "../../../../services/device.service";
import { 
  StatusLamp,
} from "./PulseDashboard";
import { 
  ShieldCheckIcon, 
  ExclamationTriangleIcon,
  NoSymbolIcon,
  ExclamationCircleIcon 
} from '@heroicons/react/24/solid';

interface Props {
  alert: Alert;
}

export default function AlertHoverPopup({ alert }: Props) {
  const { t } = useTranslation();
  const [telematicData, setTelematicData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTelematicData = async (deviceDataId: string) => {
    try {
      const response = await DeviceService.getTelematicFaultDataById(deviceDataId);
      const data = await response.json();
      setTelematicData(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching telematic data:', error);
      setError(t('common.errorFetchingData'));
      setIsLoading(false);
    }
  };

  // Fetch telematic fault data if this is a telematic fault alert
  useEffect(() => {
    // @ts-ignore
    if (alert.triggerType === 'TELEMATIC_FAULT' && alert.deviceDataId) {
      setIsLoading(true);
      setError(null);

      fetchTelematicData(alert.deviceDataId);
    }
  }, [alert.triggerType, alert.deviceDataId, t]);

  return (
    <div className="w-80 space-y-1">
      {/* Add glow styling */}
      <style>
        {`
          .glow { box-shadow: 0 0 10px currentColor; }
          .popup-lamp { transform: scale(0.6); margin: -0.75rem; }
          .popup-central { transform: scale(0.5); margin: -2rem; }
        `}
      </style>

      {/* Telematic fault specific data */}
      {/* @ts-ignore */}
      {alert.triggerType === 'TELEMATIC_FAULT' && (
        <div>
          {/* <h5 className="font-semibold text-xs mb-2">{t('sites.telematicFaultDetails')}</h5> */}
          <h5 className="font-semibold text-xs mb-2">Fault Details</h5>

          {isLoading && (
            <div className="flex justify-center py-2">
              <span className="text-sm">Loading...</span>
            </div>
          )}

          {error && (
            <p className="text-red-500 text-xs">{error}</p>
          )}

          {!isLoading && !error && telematicData && (
            <div className="space-y-2">
              {/* Status Lamps */}
              <div className="flex justify-between px-2">
                <div className="popup-lamp">
                  <StatusLamp 
                    status={telematicData?.lamps?.protect ?? 0}
                    color="yellow"
                    icon={<ShieldCheckIcon />}
                  />
                </div>
                <div className="popup-lamp">
                  <StatusLamp 
                    status={telematicData?.lamps?.warning ?? 0} 
                    color="orange"
                    icon={<ExclamationTriangleIcon />}
                  />
                </div>
                <div className="popup-lamp">
                  <StatusLamp 
                    status={telematicData?.lamps?.stop ?? 0} 
                    color="red"
                    icon={<NoSymbolIcon />}
                  />
                </div>
                <div className="popup-lamp">
                  <StatusLamp 
                    status={telematicData?.lamps?.malfunction ?? 0} 
                    color="red"
                    icon={<ExclamationCircleIcon />}
                  />
                </div>
              </div>
              
              {/* Active FMIs Table */}
              {telematicData?.faults && Object.keys(telematicData.faults).length > 0 ? (
                <div className="mt-2">
                  <h6 className="text-xs font-semibold mb-1">Active FMIs</h6>
                  <div className="overflow-x-auto max-h-24">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-2 py-1 text-left font-medium text-gray-600">FMI</th>
                          <th className="px-2 py-1 text-left font-medium text-gray-600">SPN</th>
                          <th className="px-2 py-1 text-left font-medium text-gray-600">Desc</th>
                          <th className="px-2 py-1 text-right font-medium text-gray-600">Count</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(telematicData.faults).map(([key, fault]: [string, any]) => (
                          <tr key={key} className="hover:bg-gray-50">
                            <td className="px-2 py-1 whitespace-nowrap text-gray-700">{fault.fmi}</td>
                            <td className="px-2 py-1 whitespace-nowrap text-gray-700">{fault.spn}</td>
                            <td className="px-2 py-1 whitespace-nowrap text-gray-700 truncate max-w-[100px]">{fault.name}</td>
                            <td className="px-2 py-1 whitespace-nowrap text-gray-700 text-right">{fault.oc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500 text-center">No active FMIs</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* System no data specific info if needed */}
      {alert.triggerType === 'SYSTEM_NO_DATA' && (
        <div className="mt-2 border-t border-gray-200 pt-2">
          <h5 className="font-semibold text-xs">{t('sites.connectionDetails')}</h5>
          {/* <p><strong>{t('sites.lastDataReceived')}:</strong> {alert.lastDataReceived || '-'}</p> */}
        </div>
      )}
    </div>
  );
}
